import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Download, Eye, MagnifyingGlass, Ticket as TicketIcon, Archive, PaperPlaneTilt } from '@phosphor-icons/react';

import { DashboardLayout } from '../components/DashboardLayout';
import { API, BACKEND_URL, formatApiErrorDetail } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const STATUS_OPTIONS = ['all', 'pending_approval', 'generated', 'render_failed', 'archived'];
const EMAIL_OPTIONS = ['all', 'not_configured', 'pending', 'sent', 'failed', 'skipped'];

const STATUS_LABELS = {
  pending_approval: 'Wartet auf Freigabe',
  generated: 'Erzeugt',
  render_failed: 'Renderfehler',
  archived: 'Archiviert',
};

const EMAIL_LABELS = {
  not_configured: 'Nicht konfiguriert',
  pending: 'Ausstehend',
  sent: 'Gesendet',
  failed: 'Fehlgeschlagen',
  skipped: 'Übersprungen',
};

export function TicketsPage() {
  const [tickets, setTickets] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [campaignFilter, setCampaignFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [emailFilter, setEmailFilter] = useState('all');
  const [testModeFilter, setTestModeFilter] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedTicketSubmission, setSelectedTicketSubmission] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (campaignFilter !== 'all') params.campaign_id = campaignFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (emailFilter !== 'all') params.email_status = emailFilter;
      if (testModeFilter !== 'all') params.test_mode = testModeFilter === 'test';

      const [ticketResponse, campaignResponse] = await Promise.all([
        axios.get(`${API}/tickets`, { params, withCredentials: true }),
        axios.get(`${API}/campaigns`, { withCredentials: true }),
      ]);
      setTickets(ticketResponse.data || []);
      setCampaigns(campaignResponse.data || []);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Tickets konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [campaignFilter, statusFilter, emailFilter, testModeFilter]);

  const filteredTickets = useMemo(() => tickets.filter((ticket) => {
    const query = search.trim().toLowerCase();
    if (!query) return true;
    return [ticket.serial_number, ticket.campaign_name, ticket.status, ticket.email_status]
      .filter(Boolean)
      .some((value) => value.toLowerCase().includes(query));
  }), [search, tickets]);

  const archiveTicket = async (ticket) => {
    try {
      const { data } = await axios.post(`${API}/tickets/${ticket.id}/archive`, {}, { withCredentials: true });
      toast.success('Ticket archiviert.');
      setSelectedTicket(data);
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Archivierung fehlgeschlagen.');
    }
  };

  const resendTicketEmail = async (ticket) => {
    try {
      const { data } = await axios.post(`${API}/tickets/${ticket.id}/resend-email`, {}, { withCredentials: true });
      toast.success('Ticket-Mail wurde versendet.');
      setSelectedTicket(data);
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'E-Mail-Versand fehlgeschlagen.');
      fetchData();
    }
  };

  const openTicketDetail = async (ticket) => {
    setSelectedTicket(ticket);
    setSelectedTicketSubmission(null);
    try {
      const { data } = await axios.get(`${API}/submissions/${ticket.submission_id}`, { withCredentials: true });
      setSelectedTicketSubmission(data);
    } catch (error) {
      setSelectedTicketSubmission(null);
    }
  };

  const badgeClass = (status) => ({
    pending_approval: 'bg-amber-100 text-amber-700',
    generated: 'bg-emerald-100 text-emerald-700',
    render_failed: 'bg-red-100 text-red-700',
    archived: 'bg-zinc-100 text-zinc-700',
  }[status] || 'bg-zinc-100 text-zinc-700');

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="tickets-page">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Ticket Instances</p>
          <h1 className="text-4xl font-black tracking-tight text-zinc-950">Tickets</h1>
          <p className="mt-2 text-sm text-zinc-600">Persistierte Hatıra-Bileti-Ergebnisse mit Renderstatus, Download und Wiederaufruf.</p>
        </div>

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px_180px_180px_180px]">
          <div className="relative">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Tickets durchsuchen..." className="pl-9" data-testid="tickets-search-input" />
          </div>
          <Select value={campaignFilter} onValueChange={setCampaignFilter}>
            <SelectTrigger data-testid="tickets-campaign-filter"><SelectValue placeholder="Kampagne" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Kampagnen</SelectItem>
              {campaigns.map((campaign) => (
                <SelectItem key={campaign.id} value={campaign.id}>{campaign.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="tickets-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((status) => <SelectItem key={status} value={status}>{status === 'all' ? 'Alle Status' : STATUS_LABELS[status]}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={emailFilter} onValueChange={setEmailFilter}>
            <SelectTrigger data-testid="tickets-email-filter"><SelectValue placeholder="E-Mail" /></SelectTrigger>
            <SelectContent>
              {EMAIL_OPTIONS.map((status) => <SelectItem key={status} value={status}>{status === 'all' ? 'Alle Mailstatus' : EMAIL_LABELS[status]}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={testModeFilter} onValueChange={setTestModeFilter}>
            <SelectTrigger data-testid="tickets-testmode-filter"><SelectValue placeholder="Modus" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Modi</SelectItem>
              <SelectItem value="test">Test</SelectItem>
              <SelectItem value="live">Live</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="space-y-3" data-testid="tickets-loading-state">
            {[1, 2, 3, 4].map((item) => <div key={item} className="h-16 animate-pulse rounded-sm border border-zinc-200 bg-zinc-100" />)}
          </div>
        ) : filteredTickets.length === 0 ? (
          <div className="border border-zinc-200 bg-white rounded-sm p-12 text-center" data-testid="tickets-empty-state">
            <TicketIcon className="w-12 h-12 text-zinc-300 mx-auto" />
            <p className="mt-4 text-sm text-zinc-600">Noch keine persistierten Tickets vorhanden.</p>
          </div>
        ) : (
          <div className="border border-zinc-200 bg-white rounded-sm overflow-hidden" data-testid="tickets-table">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1100px]">
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
                    <th className="px-4 py-3">Seriennummer</th>
                    <th className="px-4 py-3">Kampagne</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">E-Mail</th>
                    <th className="px-4 py-3">Modus</th>
                    <th className="px-4 py-3">Erzeugt</th>
                    <th className="px-4 py-3 text-right">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTickets.map((ticket) => (
                    <tr key={ticket.id} className="border-b border-zinc-100 last:border-b-0" data-testid={`ticket-row-${ticket.id}`}>
                      <td className="px-4 py-4 font-mono text-sm text-zinc-950">{ticket.serial_number}</td>
                      <td className="px-4 py-4 text-sm text-zinc-700">{ticket.campaign_name || ticket.campaign_id}</td>
                      <td className="px-4 py-4"><span className={`${badgeClass(ticket.status)} px-2 py-1 rounded-sm text-xs font-medium`}>{STATUS_LABELS[ticket.status] || ticket.status}</span></td>
                      <td className="px-4 py-4 text-sm text-zinc-700">{EMAIL_LABELS[ticket.email_status] || ticket.email_status}</td>
                      <td className="px-4 py-4 text-sm text-zinc-700">{ticket.test_mode ? 'Test' : 'Live'}</td>
                      <td className="px-4 py-4 text-sm text-zinc-700">{ticket.generated_at ? new Date(ticket.generated_at).toLocaleString('de-DE') : '—'}</td>
                      <td className="px-4 py-4">
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" onClick={() => openTicketDetail(ticket)} data-testid={`ticket-open-button-${ticket.id}`}>
                            <Eye className="w-4 h-4" />
                            Öffnen
                          </Button>
                          {ticket.status === 'generated' && (
                            <Button variant="outline" asChild data-testid={`ticket-download-button-${ticket.id}`}>
                              <a href={`${BACKEND_URL}/api/tickets/${ticket.id}/file`} target="_blank" rel="noopener noreferrer">
                                <Download className="w-4 h-4" />
                                PNG
                              </a>
                            </Button>
                          )}
                          {ticket.status !== 'archived' && (
                            <Button variant="outline" onClick={() => archiveTicket(ticket)} data-testid={`ticket-archive-button-${ticket.id}`}>
                              <Archive className="w-4 h-4" />
                              Archiv
                            </Button>
                          )}
                          <Button variant="outline" onClick={() => resendTicketEmail(ticket)} data-testid={`ticket-resend-button-${ticket.id}`}>
                            <PaperPlaneTilt className="w-4 h-4" />
                            Erneut senden
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <Dialog open={Boolean(selectedTicket)} onOpenChange={(open) => { if (!open) { setSelectedTicket(null); setSelectedTicketSubmission(null); } }}>
        <DialogContent className="max-w-4xl rounded-sm border-2 border-zinc-950 shadow-[8px_8px_0px_0px_rgba(9,9,11,1)]" data-testid="ticket-detail-dialog">
          <DialogHeader>
            <DialogTitle data-testid="ticket-detail-dialog-title">Ticket Detail</DialogTitle>
            <DialogDescription data-testid="ticket-detail-dialog-description">Persistiertes Ticket mit Render- und Versandzustand.</DialogDescription>
          </DialogHeader>
          {selectedTicket && (
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
              <div className="border border-zinc-200 rounded-sm overflow-hidden bg-zinc-50" data-testid="ticket-detail-preview-shell">
                {selectedTicket.status === 'generated' ? (
                  <img src={`${BACKEND_URL}/api/tickets/${selectedTicket.id}/file`} alt={selectedTicket.serial_number} className="w-full h-full object-contain bg-white" data-testid="ticket-detail-preview-image" />
                ) : (
                  <div className="min-h-[420px] flex items-center justify-center text-sm text-zinc-500">Für dieses Ticket liegt noch kein PNG vor.</div>
                )}
              </div>
              <div className="space-y-3 text-sm text-zinc-700">
                <div><span className="text-zinc-500">Seriennummer:</span> <span className="font-medium text-zinc-950">{selectedTicket.serial_number}</span></div>
                <div><span className="text-zinc-500">Status:</span> <span className="font-medium text-zinc-950">{STATUS_LABELS[selectedTicket.status] || selectedTicket.status}</span></div>
                <div><span className="text-zinc-500">E-Mail:</span> <span className="font-medium text-zinc-950">{EMAIL_LABELS[selectedTicket.email_status] || selectedTicket.email_status}</span></div>
                <div><span className="text-zinc-500">E-Mail gesendet:</span> <span className="font-medium text-zinc-950">{selectedTicket.email_sent_at ? new Date(selectedTicket.email_sent_at).toLocaleString('de-DE') : '—'}</span></div>
                <div><span className="text-zinc-500">Modus:</span> <span className="font-medium text-zinc-950">{selectedTicket.test_mode ? 'Test' : 'Live'}</span></div>
                <div><span className="text-zinc-500">E-Mail Fehler:</span> <span className="font-medium text-zinc-950 break-words">{selectedTicket.email_error || selectedTicket.last_email_error || '—'}</span></div>
                <div><span className="text-zinc-500">Renderfehler:</span> <span className="font-medium text-zinc-950 break-words">{selectedTicket.last_render_error || '—'}</span></div>
                <div><span className="text-zinc-500">Download Count:</span> <span className="font-medium text-zinc-950">{selectedTicket.download_count}</span></div>
                <div><span className="text-zinc-500">Resend Count:</span> <span className="font-medium text-zinc-950">{selectedTicket.resend_count}</span></div>
                <div><span className="text-zinc-500">Submission:</span> <span className="font-medium text-zinc-950">{selectedTicket.submission_id}</span></div>
                <div><span className="text-zinc-500">Öffentlicher Link:</span> <span className="font-medium text-zinc-950 break-all">{selectedTicket.public_view_url || '—'}</span></div>
                {selectedTicketSubmission && (
                  <div className="rounded-sm border border-zinc-200 bg-zinc-50 p-3 text-sm text-zinc-700" data-testid="ticket-detail-consent-summary">
                    <p className="font-medium text-zinc-950">Consent-Info</p>
                    <p>Zustimmung: {selectedTicketSubmission.consent_given ? 'Ja' : 'Nein'}</p>
                    <p>Zeitpunkt: {selectedTicketSubmission.consent_timestamp ? new Date(selectedTicketSubmission.consent_timestamp).toLocaleString('de-DE') : '—'}</p>
                    <p>Version: {selectedTicketSubmission.consent_version || '—'}</p>
                  </div>
                )}
                <div className="grid gap-2 pt-4">
                  {selectedTicket.status === 'generated' && (
                    <Button variant="outline" asChild data-testid="ticket-detail-download-button">
                      <a href={`${BACKEND_URL}/api/tickets/${selectedTicket.id}/file`} target="_blank" rel="noopener noreferrer">
                        <Download className="w-4 h-4" />
                        PNG herunterladen
                      </a>
                    </Button>
                  )}
                  {selectedTicket.public_view_url && (
                    <Button variant="outline" asChild data-testid="ticket-detail-public-link-button">
                      <a href={selectedTicket.public_view_url} target="_blank" rel="noopener noreferrer">
                        <Eye className="w-4 h-4" />
                        Öffentlichen Ticketlink öffnen
                      </a>
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => resendTicketEmail(selectedTicket)} data-testid="ticket-detail-resend-email-button">
                    <PaperPlaneTilt className="w-4 h-4" />
                    Erneut senden
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
